import {
    useDefaultColDef,
    gridContainerProps,
    autoSizeColumns,
    recursiveRowNodeSearch
} from "../utils/gridUtils";
import { GridRecordCount } from "../styled/DataGrid.styled";
import GridToolbar from "../components/GridToolbar";
import { AgGridReact } from "ag-grid-react";
import { useRef, useEffect, useState, useCallback } from "react";
import { useVsiAppContext } from "../stores/VsiApp";

import { customLoadingCellRenderer, integerFormatter } from "../utils/gridFormatters";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";
import { DrrDefaultValue } from "../utils/utils";
import { v4 as Uuid } from "uuid";

const countDef = {
    "field": "count",
    "type": "numericColumn",
    "cellRenderer": integerFormatter,
    "headerName": "Count",
    "filter": "agNumberColumnFilter",
    "aggFunc": "sum",
    "hide": true
};

function Grid(
    {
        addStyle,
        data,
        columnDefs,
        rowDoubleClicked,
        rowClicked,
        filterChanged,
        onGroupChanged,
        rowSelection = "single",
        hideRecordCount,
        hideGridToolbar,
        isLoading,
        gridText,
        onRowEditingStopped,
        onRowEditingStarted,
        exportCsv,
        onRefresh_click,
        style,
        onRowSelected,
        isRefreshing,
        onSelectionChanged,
        saveParams,
        customToolbarButton,
        clientSwitcher,
        height = 600,
        suppressPivotMode,
        stopEditingOnClick,
        hideColumnControls,
        disableCustomColumns,
        autoSizeStrategy,
        searchDebounce,
        noPin,
        rowGroupPanelShow = "always",
        maxHeight = 2000
    }
) {
    const [defs, setDefs] = useState(columnDefs.find(def => def.field === "count") ? [...columnDefs] : [...columnDefs, countDef]);
    const gridRef = useRef();
    const { theme, user } = useVsiAppContext();
    const [columns, setColumns] = useState(null);
    const [savedColDefs, setSavedColDefs] = useState(defs);
    const [isColumnBarOpen, setIsColumnBarOpen] = useState(false);
    const [isGrouped, setIsGrouped] = useState(false);
    const [matchingConfig, setMatchingConfig] = useState([]);
    const [rowCountDisplayed, setRowCountDisplayed] = useState("0");
    const [rowData, setRowData] = useState([]);
    const [gridId] = useState(Uuid());
    // hoisted up from GridToolbar / ColumnControls
    // selectedColumn is the selected column configuration
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [_dummyH, setHeighRender] = useState(0);

    const getGridHeight = () => {
        if (clientSwitcher) {return "55vh"}
        if (!data?.length) {return height}
        let groupCount = 0;
        gridRef?.current?.api.forEachNode(node => {
            if (node.group) {
                groupCount++;
            }
        });
        // Adjust height based on length of data * normH, limited it within min |<---->| max
        const defaultMinH = height;
        const defaultMaxH = maxHeight;
        const normalRowH = 50;
        const numRow = data.length + groupCount;
        return Math.min(Math.max(normalRowH * numRow, defaultMinH), defaultMaxH);
    }

    if (addStyle) {
        addStyle = { ...gridContainerProps(theme).style, height: height || 600 };
    } else if (style) {
        addStyle = style;
    } else {
        addStyle = { ...gridContainerProps(theme).style };
    }

    useEffect(() => {
        if (selectedColumn) {
            let matches = user.userColConfig?.filter(config => config.path === window.location.pathname && (selectedColumn.label === config.name));
            if (saveParams) {
                matches = matches.filter(config => {
                    return Object.keys(saveParams).every(key => config?.params?.[key] === saveParams?.[key]);
                });
            }

            if (matches?.length) setMatchingConfig(matches);
        } else if (matchingConfig.length) {
            setMatchingConfig([]);
        }
    }, [selectedColumn]);

    useEffect(() => {
        setDefs(columnDefs.find(def => def.field === "count") ? [...columnDefs] : [...columnDefs, countDef]);
        setColDefs([]);
        gridRef?.current?.api?.setFilterModel({});
    }, [columnDefs]);

    const setColDefs = newDefs => {
        // newDefs = matchingConfig[0].colState
        if ((newDefs?.length || matchingConfig.length) && !disableCustomColumns) {
            const staticDefs = {};
            const mobileDefs = {};
            let hasCount = false;

            defs.forEach(def => {
                if (def.field === "count") {
                    hasCount = true;
                }
                staticDefs[def.field] = { ...def };
            });

            let svColDefs = (newDefs).map(col => {
                let countConf = {};
                if (col.colId === "count") {
                    hasCount = true;
                    countConf = { ...countDef };
                }
                mobileDefs[col.colId] = { ...col };

                return {
                    ...countConf,
                    ...staticDefs[col.colId],
                    ...col,
                    aggFunc: staticDefs?.[col?.colId]?.aggFunc ?? col.aggFunc,
                    field: col.colId
                };
            });

            const allStaticFields = Object.keys(staticDefs);
            const allMobileFields = Object.keys(mobileDefs);

            // we found a field that exists in the applications column defs
            // that does not exist in the user's saved column defs.
            // time to add it to the grid
            if (!allStaticFields.every(field => allMobileFields.includes(field))) {
                // def found that doesn't exist in user's defs
                const newDefsFound = allStaticFields.filter(field => !allMobileFields.includes(field));
                if (newDefsFound.length) {
                    // filling defs with found def
                    newDefsFound.forEach(field => svColDefs.push(staticDefs[field]));
                }
            }

            // we found a field that exists in the applications column defs
            // that does not exist in the user's saved column defs.
            // time to remove it, as it should no longer be there
            if (!allMobileFields.every(field => allStaticFields.includes(field))) {
                svColDefs = svColDefs.filter(def => allStaticFields.includes(def?.field ?? def.colId));
            }
            setSavedColDefs(hasCount ? svColDefs : svColDefs.concat([countDef]));
        } else {
            let hasCount = false;
            let settingColDefs = defs?.map(colDef => {
                let countConf = {};
                if (colDef.field === "count") {
                    hasCount = true;
                    countConf = { ...countDef };
                }

                return {
                    ...colDef,
                    ...countConf,
                    hide: colDef.hide ? colDef.hide : false
                    // field: colDef.id,
                };
            });

            setSavedColDefs(hasCount ? settingColDefs : settingColDefs.concat([countDef]));
        }
    };

    useEffect(() => {
        if (!clientSwitcher) {
            // found saved defs and they're enabled for this grid
            // Apply col state, which includes grouping, and filter state
            setColDefs(matchingConfig?.[0]?.colState ?? []);
            gridRef?.current?.api?.setFilterModel(matchingConfig?.[0]?.filterState ?? {});
        }
    }, [matchingConfig, defs]);

    useEffect(() => {
        let savedConfig = [];

        if (!autoSizeStrategy && ((!savedConfig.length && matchingConfig.length) || (!matchingConfig.length))) {
            autoSizeColumns(gridRef);
        }
    }, [rowData, matchingConfig, autoSizeStrategy]);
    
    useEffect(() => {
      setRowData(
        (data || []).map(datum => ({ ...datum, count: 1 }))
      );
    }, [data]);

    const onGridGroupChanged = e => {
        if (onGroupChanged) onGroupChanged(e);
        setIsGrouped(e.columns.length > 0);
        setHeighRender(_ => _ + 1);
    };

    const loadingCellRenderer = useCallback(() => {
        return customLoadingCellRenderer;
    }, []);

    const loadingCellRendererParams = useCallback(() => {
        return {
            loadingMessage: "One moment please..."
        };
    }, []);

    const onFirstDataRendered = useCallback(() => {
        // Apply filter state here
        let savedConfig = [];
        if (saveParams) {
            savedConfig = matchingConfig.filter(config => {
                return Object.keys(saveParams).every(key => config?.params?.[key] === saveParams?.[key]);
            });
        } else {
            savedConfig = matchingConfig;
        }
        gridRef?.current?.api?.setFilterModel(savedConfig?.[0]?.filterState || {});
        // Account the savedConfig to grid height
        setHeighRender(_ => _ + 1);
    }, [matchingConfig, saveParams]);

    const weightedLatencyByIOPS = (params) => {
        // #3559  so this calc just needs to be  avg latency =sum of  (avg_latency * iops )
        // this is a line by line math for each row  and sum the results - Van Symons 6 Aug 2024
        const allData = recursiveRowNodeSearch(params.rowNode);  //Get the data from the grid
        let weightedIOPSLatency = 0;
        let sumIops = 0;
        // for each row node...
        (allData)?.forEach((rowNode) => {
            //... if there exists an iops and avg_latency field data
            if (rowNode.iops && rowNode.avg_latency) {
                //multiply avg latency times iops which is the weighting factor
                let calculatedLatency = (rowNode.avg_latency * rowNode.iops);
                weightedIOPSLatency += calculatedLatency; //add it to the running total
                sumIops += rowNode.iops;  // keep a running total of iops as the divisor for weighted avg.
            }
        });

        // if the sum of iops is 0, latency is automatically 0.  Otherwise, it is what it is:
        //   ∑ (avg_latency * iops) / ∑ iops
        // basically, you can't have latency if there are no iops.
        return sumIops > 0 ? (weightedIOPSLatency / sumIops) : 0;
    }

    const overallAvgPctOneByAnother = (params) => {
        /**
        * Calculates the average percentage for the given aggregated field in regard to valueSetting.
        * 'key' is the field being aggregated
        * 'pcfFactor' is either 1 or 100 set based on values are percent or percentage fraction
        */
        const aggregatedField = params?.colDef?.field;
        const valueSetting = {
            "percent_used": { numerator: "total_used", denominator: "usable_gb", pcfFactor: 100 },
            "pct_used": { numerator: "ttl_used", denominator: "ttl_usable", pcfFactor: 1 },
            "pool_pct_used": { numerator: "pool_used", denominator: "pool_total", pcfFactor: 100 },
            "ghz_per_cpu": { numerator: "total_mhz", denominator: "num_cpus", pcfFactor: 0.001 },
            "data_reduction_ratio": { numerator: "total_used", denominator: "physical_used", pcfFactor: 1 },
            "ballooned_memory_pct": { numerator: "ballooned_memory", denominator: "memory_allocated", pcfFactor: 1 },
            "physical_pct": { numerator: "example1", denominator: "example2", pcfFactor: 1 }
        };
        let subTotalNumerator = 0;
        let subTotalDenominator = 0;
        const allNodes = recursiveRowNodeSearch(params.rowNode);
        allNodes?.forEach(rN => {
            subTotalNumerator += rN?.[valueSetting[aggregatedField].numerator];
            subTotalDenominator += rN?.[valueSetting[aggregatedField].denominator];
        });
        if (subTotalDenominator === 0) {
            // in case we ever set for drr in future
            if (aggregatedField === "data_reduction_ratio") {
                return 1.0;
            }
            subTotalDenominator = 1; // prevent zero divider
        }
        return Number((subTotalNumerator / subTotalDenominator * valueSetting[aggregatedField].pcfFactor).toFixed(3));
    }

    const weightedDrrByPhysUsed = (params) => {
        var used = 0;
        var physUsed = 0;
        params?.values?.forEach(value => {
            if (value?.total_used) {
                used += value.total_used;
            }
            if (value?.physical_used) {
                physUsed += value.physical_used;
            }
        });
        return {
            total_used: used,
            physical_used: physUsed,
            toString: () => {
                return (used && physUsed) ? (used / physUsed).toString() : DrrDefaultValue;
            }
        };
    }

    const resize = useCallback(() => {
        autoSizeColumns(gridRef);
    }, [gridRef, autoSizeColumns]);

    const setDefaultSettings = useCallback(() => {
        setSavedColDefs(defs);
        setColumns([]);
        gridRef?.current?.api?.setRowGroupColumns([]);
        gridRef?.current?.api?.setFilterModel({});
        autoSizeColumns(gridRef);
    }, [setSavedColDefs, setColumns, gridRef, defs]);

    return <div className={"mb-100"}>
        <div className={`ag-theme-alpine${(theme === "light" ? "" : `-${theme}`)} bg-${theme} pt-2`}>
            {!hideRecordCount ? (
                <GridRecordCount className={"p-2"}>
                    {isLoading ? "Loading..." : `${data?.length?.toLocaleString?.() ?? 0} record${data?.length === 1 ? "" : `s`}`}
                    {(isLoading || parseInt(rowCountDisplayed) === parseInt(data?.length) || typeof rowCountDisplayed !== "number") ? null : `, ${rowCountDisplayed?.toLocaleString?.()} visible`}
                </GridRecordCount>
            ) : null}
            {!hideGridToolbar ? (
                <GridToolbar exportCsv={exportCsv}
                            setDefaultSettings={setDefaultSettings}
                            noPin={noPin}
                            gridId={gridId}
                            gridRef={gridRef}
                            resize={resize}
                            setColDefs={setColDefs}
                            savedColDefs={savedColDefs}
                            setSelectedColumn={setSelectedColumn}
                            selectedColumn={selectedColumn}
                            hideColumnControls={hideColumnControls}
                            gridText={gridText}
                            isGrouped={isGrouped}
                            toggleColumnBar={() => setIsColumnBarOpen(prev => !prev)}
                            isLoading={isLoading}
                            saveParams={saveParams}
                            isRefreshing={isRefreshing}
                            customToolbarButton={customToolbarButton}
                            isColumnBarOpen={isColumnBarOpen}
                            searchDebounce={searchDebounce}
                            setRowCountDisplayed={setRowCountDisplayed}
                            onRefresh_click={onRefresh_click} />
            ) : null}
        </div>
        <div {...gridContainerProps(theme)}
            style={{ ...addStyle, height:  getGridHeight(), width: "100%" }}>
            <AgGridReact
                ref={gridRef}
                loading={isLoading}
                deltaRowDataMode={true}
                getRowNodeId={data => data._id}
                onFirstDataRendered={onFirstDataRendered}
                loadingCellRenderer={loadingCellRenderer}
                loadingCellRendererParams={loadingCellRendererParams}
                onRowDoubleClicked={rowDoubleClicked}
                onRowClicked={rowClicked}
                onColumnRowGroupChanged={onGridGroupChanged}
                onFilterChanged={filterChanged}
                defaultColDef={useDefaultColDef(columns)}
                rowData={rowData}
                rowGroupPanelShow={rowGroupPanelShow}
                sideBar={isColumnBarOpen ? "columns" : false}
                columnDefs={savedColDefs}
                editType={"fullRow"}
                stopEditingWhenCellsLoseFocus={stopEditingOnClick}
                onRowEditingStopped={onRowEditingStopped || null}
                onRowEditingStarted={onRowEditingStarted || null}
                // enableRangeSelection={true}
                // groupSelectsFiltered={true}
                // groupSelectsChildren={rowSelection === "multiple" ? true : false}
                cellSelection={true}
                rowSelection={{
                    mode: (rowSelection === "multiple") ? "multiRow" : "singleRow",
                    checkboxes: false,
                    enableClickSelection: true,
                    selectAll: 'filtered',
                    groupSelects: rowSelection === "multiple" ? "descendants" : "filteredDescendants"
                }}
                onRowSelected={onRowSelected}
                onSelectionChanged={onSelectionChanged ? (() => onSelectionChanged(gridRef)) : null}
                gridOptions={{
                    pivotPanelShow: !suppressPivotMode,
                    groupDefaultExpanded: 1,
                    aggFuncs: {
                        // registered aggFunc here
                        weightedByIOPs: weightedLatencyByIOPS,
                        weightedDrr: weightedDrrByPhysUsed,
                        overallAvgPct: overallAvgPctOneByAnother
                    },
                    suppressMenuHide: false,
                    suppressAggFuncInHeader: true,  // hide sum(ColHead) as ColHead.
                    suppressScrollOnNewData: true,
                    // domLayout: clientSwitcher ? "normal" : "autoHeight",
                    autoSizeStrategy: {
                        type: autoSizeStrategy
                    }
                }}
            >
            </AgGridReact>
        </div>
    </div>;
}

Grid.displayName = "Grid";

export default withErrorBoundary(Grid, GeneralErrorBoundary);
